import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Mail, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const demoIncludes = [
    "Live demonstration with a real document",
    "See your files auto-organized in real-time",
    "Q&A with our process experts",
    "Custom implementation roadmap for your firm",
  ];

  return (
    <section
      id="book-demo"
      ref={ref}
      className="py-20 md:py-32 bg-cream relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <p className="font-body text-sm font-semibold uppercase tracking-widest text-gold mb-4">
            See It To Believe It
          </p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight tracking-tight mb-6"
            data-testid="contact-headline"
          >
            Book Your Prototype Demo
          </h2>
          <p className="font-body text-base md:text-lg text-slate-muted max-w-2xl mx-auto">
            Don't believe it? Let us process one live document for you in 15 minutes. 
            See the magic happen with your own eyes.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center max-w-4xl mx-auto">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl border border-black/5 p-8 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
              <h3 className="font-heading text-xl font-bold text-navy mb-6">
                In your personalized demo, you'll see:
              </h3>

              <div className="space-y-4 mb-8">
                {demoIncludes.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3"
                    data-testid={`demo-includes-${index}`}
                  >
                    <CheckCircle2
                      size={20}
                      className="text-gold flex-shrink-0 mt-0.5"
                    />
                    <span className="font-body text-slate-muted">{item}</span>
                  </div>
                ))}
              </div>

              {/* Trust Statement */}
              <div className="pt-6 border-t border-black/5">
                <p className="font-body text-sm text-slate-muted italic">
                  "We don't just sell promises. We show you results in real-time."
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right - Contact Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-navy rounded-2xl p-8 text-center">
              <h3 className="font-heading text-2xl font-bold text-white mb-4">
                Ready to Get Started?
              </h3>
              <p className="font-body text-white/70 mb-8">
                Reach out to schedule your free 15-minute demo
              </p>

              {/* Email CTA */}
              <a
                href="mailto:ivoryandsaffronn@gmail.com?subject=InwardSync Demo Request&body=Hi, I'm interested in scheduling a demo for InwardSync Automations."
                className="block w-full"
                data-testid="contact-email-button"
              >
                <Button
                  className="w-full bg-gold text-navy font-body font-semibold tracking-wide uppercase text-sm px-8 py-4 rounded-full transition-all duration-300 hover:bg-gold/90 active:scale-98 flex items-center justify-center gap-2"
                >
                  <Mail size={18} />
                  Email Us for Demo
                </Button>
              </a>

              {/* Contact Info */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="font-body text-sm text-white/50 mb-2">
                  Or email us directly at:
                </p>
                <a
                  href="mailto:ivoryandsaffronn@gmail.com"
                  className="font-body text-gold font-semibold hover:underline"
                  data-testid="contact-email-link"
                >
                  ivoryandsaffronn@gmail.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
